import { useNavigate, useParams } from '@tanstack/react-router';
import { Form, FormikProps, getIn, useFormikContext } from 'formik';
import { ReactNode } from 'react';

import { Iso8601DateTimeString } from '../../../types/Iso8601DateTimeString';
import { PortMessage } from '../../../types/PortMessage';
import { SamplekitMessage } from '../../../types/SamplekitMessage';
import { FormikAgentSelectFlow } from '../../agent/AgentSelectFlow/FormikAgentSelectFlow';
import { Button } from '../../cdl/Button/Button';
import { Callout } from '../../cdl/Callout/Callout';
import { FormikInput } from '../../cdl/Input/FormikInput';
import { ReadOnlyField } from '../../cdl/ReadOnlyField/ReadOnlyField';
import { Text } from '../../cdl/Text/Text';
import { Divider } from '../../common/Divider/Divider';
import { FormikDatePicker } from '../../common/form-elements/formik/FormikDatePicker';
import { FormikDebug } from '../../common/form-elements/formik/FormikDebug';
import { FormikProductTable } from '../../common/FormikProductTable/FormikProductTable';
import { startOfTimeZoneDayAsUtcIso } from '../../common/helpers/dateFns.helper';
import { formatVessel } from '../../common/helpers/formatVessel.helper';
import { translate } from '../../common/helpers/translate.helper';
import { useDocumentTitle } from '../../common/hooks/useDocumentTitle';
import { useSchedules } from '../../common/hooks/useSchedules';
import { useVesselDetails } from '../../common/hooks/useVesselDetails';
import { IconInfoCircle } from '../../common/icons/cdl/InfoCircle';
import { ItemModel } from '../../common/models/ItemModel';
import { OverlayHeader } from '../../common/Overlay/OverlayHeader';
import { Box } from '../../common/ui/Box';
import { FormikPortDetailSelect } from '../../port/portSelect/FormikPortDetailSelect';
import { OrderVesselInformation } from '../create/OrderVesselInformation';
import { useOrder } from '../hooks/useOrder';
import { OrderState } from '../model/OrderModel';

export type FormikEditOrderValues = {
    id: string;
    vesselId: string;
    customerId: string;
    portId: string;
    dateDelivery: Iso8601DateTimeString | null;
    buyerReference: string;
    agentId: string;
    items: ItemModel[];
    samplekits: SamplekitMessage[];
    receiverSupplierIds: string[];
    spot: boolean;
    state?: OrderState;
};

interface PageWithHeaderProps {
    children: ReactNode;
}

const PageWithHeader = ({ children }: PageWithHeaderProps) => {
    const { id } = useParams({ from: '/_app/order_/edit/$id' });
    const navigate = useNavigate({ from: '/order/edit/$id' });

    const { data: order } = useOrder(id);
    const { isSubmitting, dirty } = useFormikContext();

    useDocumentTitle(translate('page.orderedit', { orderNumber: id }));

    const callIfConfirmedDirty = (fn: Function) => () => {
        if (dirty && !confirm(translate('confirmation.warning'))) {
            return;
        }

        fn();
    };

    return (
        <OverlayHeader
            title={translate('order.edit.headline', { orderId: order?.orderNumber || '' })}
            onClose={callIfConfirmedDirty(() => navigate({ to: '/order/$id', params: { id } }))}
            trailingVisual={
                <Box gap={8}>
                    <Button type="submit" emphasis="high" isLoading={isSubmitting}>
                        {translate('order.edit.update')}
                    </Button>
                </Box>
            }
        >
            {children}
        </OverlayHeader>
    );
};

export const OrderEditForm = (props: FormikProps<FormikEditOrderValues>) => {
    const { data: vessel } = useVesselDetails(props.values.vesselId);
    const { schedules } = useSchedules({ imo: vessel?.imo });
    const { setFieldError, setFieldValue, errors, touched, submitCount } = useFormikContext<FormikEditOrderValues>();
    const duplicateItemsError = getIn(errors, 'duplicateItemsError');
    const showDuplicateItemsError = Boolean(duplicateItemsError && (submitCount > 0 || touched?.items));

    const onPortSelect = (port: PortMessage, scheduleRank?: number) => {
        setFieldValue('agentId', undefined, false);

        const matchingSchedule = schedules.find(
            (schedule) => schedule.port?.id === port.id && schedule.rank === scheduleRank
        );

        if (matchingSchedule) {
            setFieldValue(
                'dateDelivery',
                startOfTimeZoneDayAsUtcIso(matchingSchedule.eta, matchingSchedule.timeZoneId),
                false
            );
            setFieldError('dateDelivery', undefined);

            if (matchingSchedule.agentIds?.length === 1) {
                setFieldValue('agentId', matchingSchedule.agentIds[0], false);
            }
        }
    };

    return (
        <Form>
            <PageWithHeader>
                <Box padding={32} display="flex" flexDirection="column">
                    <div>
                        <Text variant="title">{translate('order.coreData')}</Text>
                        <Text variant="body" color="foreground.muted" as="p">
                            {translate('order.coreDataSubText')}
                        </Text>
                    </div>
                    <Divider marginTop={8} marginBottom={32} />
                    <Box display="flex" gap={48} flexDirection="column">
                        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={40} marginBottom={24}>
                            <Box display="flex" gap={16} flexDirection="column">
                                <ReadOnlyField
                                    value={formatVessel({ vessel, short: true })}
                                    label={translate('global.coreData.vessel')}
                                />

                                <FormikPortDetailSelect name="portId" schedule={schedules} onSelect={onPortSelect} />

                                <FormikDatePicker name="dateDelivery" label={translate('order.dateDelivery')} />

                                <FormikInput
                                    label={translate('order.buyerReferenceOptional')}
                                    name="buyerReference"
                                    markAsOptional
                                />

                                <FormikAgentSelectFlow
                                    label={translate('order.agentOptional')}
                                    customerId={props.values.customerId}
                                    portId={props.values.portId}
                                    name="agentId"
                                    markAsOptional
                                />
                            </Box>

                            <OrderVesselInformation vesselId={props.values.vesselId} />
                        </Box>

                        <Box>
                            <Text variant="title">{translate('order.products')}</Text>
                            <Divider marginTop={16} marginBottom={32} />
                            <FormikProductTable
                                config={{
                                    initialItems: [],
                                    editable: true,
                                    showPrices: false,
                                    editSurcharges: false,
                                    editPrices: false,
                                }}
                            />
                            {showDuplicateItemsError ? (
                                <Box marginTop={16}>
                                    <Callout
                                        variant="negative"
                                        description={duplicateItemsError}
                                        Icon={IconInfoCircle}
                                    />
                                </Box>
                            ) : null}
                        </Box>
                    </Box>
                    <FormikDebug />
                </Box>
            </PageWithHeader>
        </Form>
    );
};
